import { Button, Segmented, Select, Space } from 'antd'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  getPrimitiveGeometryOptions,
  KVPrimitiveGeometry,
  type KVPrimitiveGeometryType,
} from 'types'

import { MANUAL_PLACEMENT_INSTRUCTIONS } from './primitiveBuilderTools.constants'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const ControlsContainer = styled.div`
  position: absolute;
  top: 3.75rem;
  right: 1.25rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 14rem;
  width: 16rem;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.25rem;
`

const StyledSpace = styled(Space)`
  width: 100%;
`

const StyledButtonRow = styled(Space)`
  width: 100%;
  display: flex;
  gap: 0.5rem;
`

const StyledSegmented = styled(Segmented)`
  width: 100%;
  display: flex;

  .ant-segmented-group {
    display: flex;
    width: 100%;
  }

  .ant-segmented-item {
    flex: 1;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    border: none;
    transition: all 0.2s ease;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .ant-segmented-item-selected {
    background: rgba(56, 158, 13, 0.9) !important;
    color: white !important;
  }

  .ant-segmented-thumb {
    background: rgba(56, 158, 13, 0.9);
  }
`

const InstructionsContainer = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.375rem;
  border-left: 2px solid rgba(56, 158, 13, 0.7);
`

const InstructionsTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const InstructionsList = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;

  li {
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

type PlacementMode = 'auto' | 'manual'

export const PrimitiveBuilderTools: React.FC = () => {
  const { sendCommand } = useKestrelViewerContext()
  const [primitive, setPrimitive] = useState<KVPrimitiveGeometryType>(
    KVPrimitiveGeometry.BOX
  )
  const [placementMode, setPlacementMode] = useState<PlacementMode>('auto')

  const options = useMemo(() => getPrimitiveGeometryOptions(), [])

  const handlePlacementModeChange = useCallback(
    (mode: PlacementMode) => {
      setPlacementMode(mode)

      if (mode === 'manual') {
        sendCommand({
          command: KestrelCommands.SET_PLACEMENT_MODE,
          mode: 'manual',
        })
        sendCommand({
          command: KestrelCommands.START_MANUAL_PLACEMENT,
          primitiveType: primitive,
        })
      } else {
        sendCommand({
          command: KestrelCommands.SET_PLACEMENT_MODE,
          mode: 'autofit',
        })
      }
    },
    [sendCommand, primitive]
  )

  useEffect(() => {
    if (placementMode === 'manual') {
      sendCommand({
        command: KestrelCommands.START_MANUAL_PLACEMENT,
        primitiveType: primitive,
      })
    }
  }, [primitive, placementMode, sendCommand])

  const handleFit = useCallback(() => {
    sendCommand({
      command: KestrelCommands.FIT_PRIMITIVE,
      primitiveType: primitive,
    })
  }, [sendCommand, primitive])

  const handleClear = useCallback(() => {
    sendCommand({
      command: KestrelCommands.CLEAR_CURRENT_SEGMENT,
    })
  }, [sendCommand])

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  return (
    <ControlsContainer onClick={stopPropagation} onMouseDown={stopPropagation}>
      <ControlLabel>Primitive</ControlLabel>
      <StyledSpace orientation='vertical' size='small'>
        <Select
          value={primitive}
          options={options}
          onChange={(v) => setPrimitive(v)}
          popupClassName='kestrel-primitive-dropdown'
          dropdownStyle={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
          style={{ width: '100%' }}
        />
        <ControlLabel style={{ marginTop: '0.5rem' }}>Fit Mode</ControlLabel>
        <StyledSegmented
          block
          value={placementMode}
          onChange={(value) =>
            handlePlacementModeChange(value as PlacementMode)
          }
          options={[
            { label: 'Auto', value: 'auto' },
            { label: 'Manual', value: 'manual' },
          ]}
        />
        <StyledButtonRow>
          <Button
            type='primary'
            onClick={handleFit}
            disabled={placementMode === 'manual'}
            style={{
              flex: 1,
              opacity: placementMode === 'manual' ? 0.5 : 1,
              cursor: placementMode === 'manual' ? 'not-allowed' : 'pointer',
            }}>
            Fit
          </Button>
          <Button type='primary' onClick={handleClear} style={{ flex: 1 }}>
            Clear
          </Button>
        </StyledButtonRow>
        {placementMode === 'manual' && (
          <InstructionsContainer>
            <InstructionsTitle>Instructions</InstructionsTitle>
            <InstructionsList>
              {MANUAL_PLACEMENT_INSTRUCTIONS[primitive].map(
                (instruction, index) => (
                  <li key={index}>{instruction}</li>
                )
              )}
            </InstructionsList>
          </InstructionsContainer>
        )}
      </StyledSpace>
    </ControlsContainer>
  )
}
